import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartDomReadyEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #pendingOpen = false;
  
  /** @type {number | null} */
  #fallbackTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartDomReady, this.#handleCartDomReady);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartDomReady, this.#handleCartDomReady);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    if (this.#fallbackTimeout) {
      clearTimeout(this.#fallbackTimeout);
    }
  }

  /** @param {CartAddEvent} event */
  #handleCartAdd = (event) => {
    // Don't open cart drawer if there was an error (e.g., max quantity reached)
    if (event?.detail?.data?.didError === true) {
      return;
    }
    
    // Only auto-open for product-form-component source (add to cart), not for cart updates
    const source = event?.detail?.data?.source;
    if (source !== 'product-form-component') {
      return;
    }
    
    if (!this.hasAttribute('auto-open')) {
      return;
    }
    
    // Mark that we're waiting for the DOM to be ready before opening
    // The cart-items-component will dispatch CartDomReadyEvent after morph completes
    this.#pendingOpen = true;
    
    // Fallback: if CartDomReadyEvent doesn't fire within 500ms, open anyway
    if (this.#fallbackTimeout) {
      clearTimeout(this.#fallbackTimeout);
    }
    this.#fallbackTimeout = setTimeout(() => {
      if (this.#pendingOpen && !this.refs.dialog?.open) {
        this.#pendingOpen = false;
        this.showDialog();
      }
    }, 500);
  };
  
  /** @param {CartDomReadyEvent} event */
  #handleCartDomReady = (event) => {
    // Only open if we're waiting for it (from a product-form add to cart)
    if (!this.#pendingOpen) {
      return;
    }
    
    // Only open for product-form-component source
    const source = event?.detail?.data?.source;
    if (source !== 'product-form-component') {
      return;
    }
    
    // Don't open if already open
    if (this.refs.dialog?.open) {
      this.#pendingOpen = false;
      return;
    }
    
    // Clear the pending flag and open the drawer
    this.#pendingOpen = false;
    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
